import { useMemo } from 'react';
import { useOrgMe } from '../organization';
import { iamProjectToProject } from '../projects';
import { iamTeamToTeam } from '../teams';
import { useIamProjects, useIamTeams } from './queries';

// The Teams and Projects pages of the organizations console list what the signed-in user
// belongs to. A person who may list the whole organization (teams.list / projects.list at
// organization level) should see all of it, so these hooks add the rest from the IAM API.
// An entry the user belongs to keeps the legacy row, which carries their role in it.

/** DataCentral customer id of the user's organization, when known. */
export function useOrgCustomerId(): string | undefined {
  const { data: me } = useOrgMe();
  return me?.customer_id ? String(me.customer_id) : undefined;
}

/** Teams the caller can see: all of them with an org-wide right, otherwise the ones they belong to. */
export function useVisibleTeams() {
  const all = useIamTeams(undefined, { enabled: true });
  const data = useMemo(
    () => (all.data ?? []).map(iamTeamToTeam).sort((x, y) => x.team_name.localeCompare(y.team_name)),
    [all.data],
  );
  return { data, isLoading: all.isLoading };
}

/** Projects the caller can see, archived ones included: those they hold a role on, or all with an org-wide role. */
export function useVisibleProjects() {
  const org = useOrgCustomerId();
  const all = useIamProjects(org, true);
  const data = useMemo(
    () => (all.data ?? []).map(iamProjectToProject).sort((x, y) => x.name.localeCompare(y.name)),
    [all.data],
  );
  return { data, isLoading: all.isLoading };
}
